import config from 'lib/config'
import { logger } from 'lib/utils/logger'

import {
  DEMO_EVENT_PREFIX,
  GA_COMMAND_CONFIG,
  GA_COMMAND_EVENT,
  GA_COMMAND_JS,
  GA_COMMAND_SET,
  GA_EVENT_NAME_MAX_LENGTH,
  GA_EVENT_NAME_OVERRIDES,
  GA_MAX_PARAMS_PER_EVENT,
  GA_PAGE_VIEW_EVENT,
  GA_PARAM_NAME_MAX_LENGTH,
  GA_PARAM_VALUE_MAX_LENGTH,
  GA_PII_KEYS,
  GA_SCRIPT_ID,
  GA_USER_ID_KEY,
} from './constants'

type GaPayload = Record<string, unknown>
type GaParams = Record<string, string | number | boolean>

// An empty measurement id means GA is switched off for this environment (every
// env except production/staging_prod ships blank). Nothing is injected and no
// command is queued, so a non-live build never talks to Google.
const isEnabled = (): boolean => Boolean(config.GA_MEASUREMENT_ID)

// Maps a trackEvent name onto a name GA4 will actually accept. GA silently
// discards events whose name exceeds 40 characters, and 37 of our event names
// are longer than that. Explicit overrides come first (those are the names that
// would otherwise truncate into each other); everything else is cut to the
// limit. googleAnalytics.test.ts asserts the result is unique across every
// event, so a future long name cannot quietly merge into an existing one.
export const getGoogleAnalyticsEventName = (eventName: string): string => {
  const override = GA_EVENT_NAME_OVERRIDES[eventName]
  return (override || eventName || '').slice(0, GA_EVENT_NAME_MAX_LENGTH)
}

// GA4 only stores primitives, so objects/arrays are serialised rather than
// dropped — otherwise a payload like { filters: [...] } would arrive empty.
const toGaValue = (value: unknown): string | number | boolean => {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value.slice(0, GA_PARAM_VALUE_MAX_LENGTH)
  }
  try {
    return JSON.stringify(value)?.slice(0, GA_PARAM_VALUE_MAX_LENGTH) || ''
  } catch (error) {
    return ''
  }
}

// Builds the GA copy of a trackEvent payload: PII removed (see GA_PII_KEYS),
// empty values dropped, names/values cut to GA's limits, and the whole thing
// capped at 25 parameters. The original payload object is never mutated — the
// PostHog and dataLayer providers still send it in full.
export const sanitizeGoogleAnalyticsPayload = (
  payload: GaPayload = {}
): GaParams => {
  const params: GaParams = {}
  const entries = Object.entries(payload || {}).filter(
    ([key, value]) =>
      key &&
      !GA_PII_KEYS.includes(key) &&
      value !== undefined &&
      value !== null &&
      value !== ''
  )

  entries.slice(0, GA_MAX_PARAMS_PER_EVENT).forEach(([key, value]) => {
    params[key.slice(0, GA_PARAM_NAME_MAX_LENGTH)] = toGaValue(value)
  })

  if (entries.length > GA_MAX_PARAMS_PER_EVENT) {
    logger(
      'GA4: parameters over the per-event limit were dropped',
      entries.slice(GA_MAX_PARAMS_PER_EVENT).map(([key]) => key)
    )
  }

  return params
}

// Queues a gtag command. gtag reads its arguments off the shared dataLayer, so
// this is a no-op until the shim in initGoogleAnalytics has run.
const sendCommand = (...args: unknown[]): void => {
  if (!isEnabled()) {
    return
  }
  window?.gtag?.(...args)
}

export const initGoogleAnalytics = (): void => {
  if (!isEnabled() || window.document.getElementById(GA_SCRIPT_ID)) {
    return
  }

  const script = window.document.createElement('script')
  script.id = GA_SCRIPT_ID
  script.async = true
  script.src = `${config.GA_SCRIPT_HOST}?id=${config.GA_MEASUREMENT_ID}`
  window.document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  // Deliberately not an arrow function: gtag identifies its commands by pushing
  // the raw `arguments` object, which arrow functions do not have. This mirrors
  // Google's own snippet — swapping in an arrow (or a rest array) changes what
  // lands on the dataLayer and GA stops recognising the commands.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }

  window.gtag(GA_COMMAND_JS, new Date())
  // send_page_view is off because PageViewTracker emits a page_view on every
  // route change, including the first one. Left on, GA's automatic initial
  // pageview would double-count the entry page of every session.
  window.gtag(GA_COMMAND_CONFIG, config.GA_MEASUREMENT_ID, {
    send_page_view: false,
  })
}

export const trackGoogleAnalyticsEvent = (
  eventName: string,
  payload: GaPayload = {}
): void => {
  // Demo mirror events (demo_<name>) are a PostHog-side convenience and carry
  // nothing the real event does not: GA already receives the same event with
  // is_demo_store on it, so demo traffic segments on that parameter. Skipping
  // them keeps GA volume off double for demo stores, and avoids the prefix
  // pushing already-40-character names past GA's limit into each other.
  if (eventName?.startsWith(DEMO_EVENT_PREFIX)) {
    return
  }

  sendCommand(
    GA_COMMAND_EVENT,
    getGoogleAnalyticsEventName(eventName),
    sanitizeGoogleAnalyticsPayload(payload)
  )
}

export const trackGoogleAnalyticsPageView = (pathname: string): void => {
  sendCommand(GA_COMMAND_EVENT, GA_PAGE_VIEW_EVENT, {
    page_path: pathname,
    page_location: window?.location?.href,
    page_title: window?.document?.title,
  })
}

// GA's user_id is the one identifier Google expects to be an internal id, so
// the agent UUID is safe here in a way email/name are not.
export const identifyGoogleAnalyticsUser = (userId: string): void => {
  if (!userId) {
    return
  }
  sendCommand(GA_COMMAND_SET, { [GA_USER_ID_KEY]: userId })
}

// Clears the identity on sign-out so the next agent on the same browser is not
// attributed to the previous one.
export const resetGoogleAnalyticsUser = (): void => {
  sendCommand(GA_COMMAND_SET, { [GA_USER_ID_KEY]: null })
}
